import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { DeptShelf } from "./LearningHubConcept2";
import Concept2Modal from "./concept2/Concept2Modal";
import Concept2Viewer from "./concept2/Concept2Viewer";
import "./LearningHubConcept2.css";

/* Dedicated Concept 2 sub-page — shows every collection owned by a
 *   single department using the same DeptShelf + Concept2Modal that
 *   the Learning Hub grid uses, so users get identical UI/UX.
 *   `hideViewAll` disables the "View all N" split menu (we already are
 *   on the "all" page) while keeping every other affordance intact. */
const LearningDepartmentPage = () => {
    const { deptId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { depts, settings } = useData();
    const autoCovers = !!settings?.learning_auto_covers;

    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCollection, setActiveCollection] = useState(null);
    const [activeResource, setActiveResource] = useState(null);

    const dept = useMemo(() => depts.find((d) => d.id === deptId), [depts, deptId]);
    const deptName = dept?.alias || dept?.name || "Department";

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/learning/collections");
            const list = (res.data || []).filter((c) => c.owner_department_id === deptId);
            setCollections(list);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load collections");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [deptId]);

    const canManage = (c) => {
        if (!user) return false;
        if (user.role === "super_admin") return true;
        if (user.role === "admin" && (c.owner_department_id === user.department_id || !c.owner_department_id)) return true;
        // Any teammate in the owning department can curate shared collections.
        if (user.department_id && c.owner_department_id && c.owner_department_id === user.department_id) return true;
        return c.created_by === user.user_id;
    };

    return (
        <div className="lh-c2-root">
            <div className="lh-c2-container" style={{ paddingTop: 24 }}>
                <button
                    className="lh-c2-btn"
                    onClick={() => navigate("/learning")}
                    data-testid="lh-c2-dept-back"
                    style={{ marginBottom: 16 }}
                >
                    <ArrowLeft size={14} /> Back to Learning Hub
                </button>

                <header style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-soft)", fontWeight: 700 }}>
                        Department · All collections
                    </div>
                    <h1 style={{ fontFamily: "'Lora', serif", fontSize: 32, fontWeight: 600, marginTop: 4, color: "var(--navy)" }}>
                        {deptName}
                    </h1>
                    <p style={{ color: "var(--ink-soft)", marginTop: 4, fontSize: 14 }}>
                        {collections.length} {collections.length === 1 ? "collection" : "collections"} owned by this department
                    </p>
                </header>

                {loading ? (
                    <div style={{ padding: 60, textAlign: "center", color: "var(--ink-soft)" }} data-testid="lh-c2-dept-loading">
                        <Loader2 className="animate-spin" size={26} style={{ margin: "0 auto 12px" }} />
                        <div>Loading collections…</div>
                    </div>
                ) : collections.length === 0 ? (
                    <div className="lh-c2-empty" data-testid="lh-c2-dept-empty">
                        <p>This department doesn&apos;t have any collections yet.</p>
                    </div>
                ) : (
                    <DeptShelf
                        deptId={deptId}
                        deptName={deptName}
                        collections={collections}
                        maxCards={collections.length}
                        expanded={true}
                        onToggle={() => {}}
                        onOpen={setActiveCollection}
                        onEdit={() => toast.info("Edit is available from the Learning Hub grid")}
                        onDelete={() => toast.info("Delete is available from the Learning Hub grid")}
                        onAssign={() => toast.info("Assign is available from the Learning Hub grid")}
                        onReroll={() => toast.info("Reroll is available from the Learning Hub grid")}
                        canManage={canManage}
                        autoCovers={autoCovers}
                        hideViewAll
                    />
                )}
            </div>

            {activeCollection && (
                <Concept2Modal
                    collection={activeCollection}
                    onClose={() => setActiveCollection(null)}
                    onOpenResource={(r, col) => setActiveResource({ resource: r, collection: col })}
                    onChanged={load}
                    autoCovers={autoCovers}
                />
            )}
            {activeResource && (
                <Concept2Viewer
                    resource={activeResource.resource}
                    collection={activeResource.collection}
                    onClose={() => setActiveResource(null)}
                />
            )}
        </div>
    );
};

export default LearningDepartmentPage;
